import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, Star, Truck, RotateCcw, Shield, Ruler } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 25: SAHAJ
 * Style: Clean Contemporary Indian
 * Best for: Modern Indian fashion, Indo-Western wear, Contemporary ethnic brands
 * Black/white with bold teal accent, sharp edges, strong typography
 * Inspired by Aadaran.com
 */
const Template25 = () => {
    const navigate = useNavigate();
    const [wishlist, setWishlist] = useState({});
    const [email, setEmail] = useState('');
    const [activeCategory, setActiveCategory] = useState(0);
    const categoryRef = useRef(null);

    // Categories
    const categories = [
        { name: 'Kurtas', image: 'https://images.pexels.com/photos/8816440/pexels-photo-8816440.jpeg?auto=compress&cs=tinysrgb&w=600' },
        { name: 'Sherwanis', image: 'https://images.pexels.com/photos/6687182/pexels-photo-6687182.jpeg?auto=compress&cs=tinysrgb&w=600' },
        { name: 'Lehengas', image: 'https://images.pexels.com/photos/12995512/pexels-photo-12995512.jpeg?auto=compress&cs=tinysrgb&w=600' },
        { name: 'Indo-Western', image: 'https://images.pexels.com/photos/32151740/pexels-photo-32151740.jpeg?auto=compress&cs=tinysrgb&w=600' },
        { name: 'Sarees', image: 'https://images.pexels.com/photos/28867973/pexels-photo-28867973.jpeg?auto=compress&cs=tinysrgb&w=600' },
        { name: 'Accessories', image: 'https://images.pexels.com/photos/3221176/pexels-photo-3221176.jpeg?auto=compress&cs=tinysrgb&w=600' },
    ];

    // Products
    const products = [
        { id: 1, name: 'Classic Cotton Kurta', price: '\u20B93,499', image: 'https://images.pexels.com/photos/8816440/pexels-photo-8816440.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 2, name: 'Modern Sherwani Set', price: '\u20B918,999', image: 'https://images.pexels.com/photos/6687182/pexels-photo-6687182.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 3, name: 'Silk Blend Saree', price: '\u20B99,500', image: 'https://images.pexels.com/photos/2723623/pexels-photo-2723623.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 4, name: 'Indo-Western Jacket', price: '\u20B97,800', image: 'https://images.pexels.com/photos/32151740/pexels-photo-32151740.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 5, name: 'Linen Kurta Pajama', price: '\u20B94,200', image: 'https://images.pexels.com/photos/17611651/pexels-photo-17611651.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 6, name: 'Contemporary Lehenga', price: '\u20B914,999', image: 'https://images.pexels.com/photos/12995512/pexels-photo-12995512.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 7, name: 'Structured Nehru Jacket', price: '\u20B96,500', image: 'https://images.pexels.com/photos/34423732/pexels-photo-34423732.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 8, name: 'Chanderi Silk Dupatta', price: '\u20B93,200', image: 'https://images.pexels.com/photos/5595710/pexels-photo-5595710.jpeg?auto=compress&cs=tinysrgb&w=800' },
    ];

    // Bestsellers
    const bestsellers = [
        { id: 101, name: 'Heritage Silk Kurta Set', price: '\u20B98,999', rating: 4.9, reviews: 128, image: 'https://images.pexels.com/photos/9419032/pexels-photo-9419032.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 102, name: 'Royal Sherwani Collection', price: '\u20B922,500', rating: 4.8, reviews: 96, image: 'https://images.pexels.com/photos/9927983/pexels-photo-9927983.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 103, name: 'Handwoven Banarasi Saree', price: '\u20B916,999', rating: 5.0, reviews: 74, image: 'https://images.pexels.com/photos/11707056/pexels-photo-11707056.jpeg?auto=compress&cs=tinysrgb&w=800' },
    ];

    // Stats
    const stats = [
        { number: '5000+', label: 'Happy Customers' },
        { number: '200+', label: 'Designs' },
        { number: 'Free', label: 'Shipping All India' },
        { number: '100%', label: 'Handcrafted' },
    ];

    // Service features
    const services = [
        { icon: Truck, title: 'Free Shipping', desc: 'On all orders above \u20B91,999' },
        { icon: RotateCcw, title: 'Easy Returns', desc: '15-day hassle-free returns' },
        { icon: Shield, title: 'Secure Payment', desc: '100% secure transactions' },
        { icon: Ruler, title: 'Custom Sizing', desc: 'Made to your measurements' },
    ];

    const toggleWishlist = (e, id) => {
        e.stopPropagation();
        setWishlist((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    // Scroll active category into view
    useEffect(() => {
        if (categoryRef.current) {
            const container = categoryRef.current;
            const activeEl = container.children[activeCategory];
            if (activeEl) {
                const containerRect = container.getBoundingClientRect();
                const elRect = activeEl.getBoundingClientRect();
                if (elRect.left < containerRect.left || elRect.right > containerRect.right) {
                    activeEl.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                }
            }
        }
    }, [activeCategory]);

    return (
        <div style={{ fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif" }} className="w-full min-h-screen bg-white">
            {/* Google Fonts */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap');
                .sahaj-no-scrollbar::-webkit-scrollbar { display: none; }
                .sahaj-no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>

            {/* ===== SECTION 1: BOLD TYPOGRAPHIC HERO ===== */}
            {/* Mobile */}
            <section className="lg:hidden" style={{ backgroundColor: '#FFFFFF' }}>
                <div className="px-5 pt-24 pb-10">
                    <p
                        className="text-xs tracking-[0.3em] uppercase mb-4"
                        style={{
                            color: '#0D7C66',
                            fontFamily: "'IBM Plex Sans', sans-serif",
                            fontWeight: 600,
                        }}
                    >
                        Contemporary Indian Fashion
                    </p>
                    <h1
                        className="text-5xl font-bold mb-5 leading-[0.95]"
                        style={{
                            fontFamily: "'Space Grotesk', sans-serif",
                            color: '#111111',
                            letterSpacing: '-0.02em',
                        }}
                    >
                        Redefining
                        <br />
                        Indian
                        <br />
                        Fashion
                    </h1>
                    <p
                        className="text-base font-light mb-8 max-w-sm leading-relaxed"
                        style={{ color: '#6C757D' }}
                    >
                        Where tradition meets minimalism. Clean silhouettes, honest fabrics, rooted in Indian craft.
                    </p>
                    <button
                        onClick={() => navigate('/template25/shop')}
                        className="inline-flex items-center gap-3 px-8 py-4 text-sm tracking-[0.15em] uppercase font-semibold transition-all duration-300"
                        style={{
                            backgroundColor: '#0D7C66',
                            color: '#FFFFFF',
                            borderRadius: 0,
                            border: 'none',
                            fontFamily: "'IBM Plex Sans', sans-serif",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#0A9B7F'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#0D7C66'; }}
                    >
                        Shop Now <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
                <div className="w-full aspect-[4/5] overflow-hidden">
                    <img
                        src="https://images.pexels.com/photos/8816440/pexels-photo-8816440.jpeg?auto=compress&cs=tinysrgb&w=1260"
                        alt="Contemporary Indian Fashion"
                        className="w-full h-full object-cover"
                    />
                </div>
            </section>

            {/* Desktop Hero */}
            <section className="hidden lg:flex items-center min-h-screen" style={{ backgroundColor: '#FFFFFF' }}>
                <div className="max-w-7xl mx-auto px-8 w-full grid grid-cols-2 gap-16 items-center">
                    {/* Left: Typography */}
                    <div>
                        <p
                            className="text-xs tracking-[0.4em] uppercase mb-6"
                            style={{
                                color: '#0D7C66',
                                fontFamily: "'IBM Plex Sans', sans-serif",
                                fontWeight: 600,
                            }}
                        >
                            Contemporary Indian Fashion
                        </p>
                        <h1
                            className="text-7xl xl:text-8xl font-bold mb-6 leading-[0.92]"
                            style={{
                                fontFamily: "'Space Grotesk', sans-serif",
                                color: '#111111',
                                letterSpacing: '-0.03em',
                            }}
                        >
                            Redefining
                            <br />
                            Indian
                            <br />
                            Fashion
                        </h1>
                        <p
                            className="text-lg font-light mb-10 max-w-md leading-relaxed"
                            style={{ color: '#6C757D' }}
                        >
                            Where tradition meets minimalism. Clean silhouettes, honest fabrics, rooted in Indian craft.
                        </p>
                        <button
                            onClick={() => navigate('/template25/shop')}
                            className="inline-flex items-center gap-3 px-10 py-4.5 text-sm tracking-[0.2em] uppercase font-semibold transition-all duration-300"
                            style={{
                                backgroundColor: '#0D7C66',
                                color: '#FFFFFF',
                                borderRadius: 0,
                                border: 'none',
                                fontFamily: "'IBM Plex Sans', sans-serif",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#0A9B7F'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#0D7C66'; }}
                        >
                            Shop Now <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Right: Image */}
                    <div className="aspect-[3/4] overflow-hidden" style={{ borderRadius: 0 }}>
                        <img
                            src="https://images.pexels.com/photos/8816440/pexels-photo-8816440.jpeg?auto=compress&cs=tinysrgb&w=1260"
                            alt="Contemporary Indian Fashion"
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                        />
                    </div>
                </div>
            </section>

            {/* ===== SECTION 2: CATEGORY STRIP ===== */}
            <section style={{ backgroundColor: '#F8F9FA' }}>
                {/* Mobile: horizontal scroll */}
                <div className="lg:hidden py-8">
                    <h2
                        className="text-xl font-bold mb-6 px-5"
                        style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#111111' }}
                    >
                        Shop by Category
                    </h2>
                    <div
                        ref={categoryRef}
                        className="flex gap-4 px-5 overflow-x-auto sahaj-no-scrollbar"
                    >
                        {categories.map((cat, index) => (
                            <div
                                key={index}
                                onClick={() => {
                                    setActiveCategory(index);
                                    navigate('/template25/shop');
                                }}
                                className="flex-shrink-0 cursor-pointer group"
                                style={{ width: '140px' }}
                            >
                                <div
                                    className="relative w-full aspect-square overflow-hidden mb-3"
                                    style={{
                                        borderRadius: 0,
                                        border: activeCategory === index ? '2px solid #0D7C66' : '2px solid transparent',
                                    }}
                                >
                                    <img
                                        src={cat.image}
                                        alt={cat.name}
                                        className="w-full h-full object-cover"
                                    />
                                    {activeCategory === index && (
                                        <div
                                            className="absolute inset-0"
                                            style={{ backgroundColor: 'rgba(13, 124, 102, 0.15)' }}
                                        />
                                    )}
                                </div>
                                <p
                                    className="text-xs font-semibold tracking-[0.1em] uppercase text-center"
                                    style={{
                                        color: activeCategory === index ? '#0D7C66' : '#111111',
                                        fontFamily: "'IBM Plex Sans', sans-serif",
                                    }}
                                >
                                    {cat.name}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Desktop: full row */}
                <div className="hidden lg:block py-16">
                    <div className="max-w-7xl mx-auto px-8">
                        <h2
                            className="text-2xl font-bold mb-10"
                            style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#111111' }}
                        >
                            Shop by Category
                        </h2>
                        <div className="grid grid-cols-6 gap-6">
                            {categories.map((cat, index) => (
                                <div
                                    key={index}
                                    onClick={() => {
                                        setActiveCategory(index);
                                        navigate('/template25/shop');
                                    }}
                                    className="cursor-pointer group"
                                >
                                    <div
                                        className="relative w-full aspect-square overflow-hidden mb-3 transition-all duration-300"
                                        style={{
                                            borderRadius: 0,
                                            border: activeCategory === index ? '2px solid #0D7C66' : '2px solid transparent',
                                        }}
                                    >
                                        <img
                                            src={cat.image}
                                            alt={cat.name}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                        {activeCategory === index && (
                                            <div
                                                className="absolute inset-0"
                                                style={{ backgroundColor: 'rgba(13, 124, 102, 0.15)' }}
                                            />
                                        )}
                                    </div>
                                    <p
                                        className="text-sm font-semibold tracking-[0.1em] uppercase text-center transition-colors duration-300"
                                        style={{
                                            color: activeCategory === index ? '#0D7C66' : '#111111',
                                            fontFamily: "'IBM Plex Sans', sans-serif",
                                        }}
                                    >
                                        {cat.name}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 3: PRODUCT GRID ===== */}
            {/* Mobile: 2 columns */}
            <section className="lg:hidden py-10" style={{ backgroundColor: '#FFFFFF' }}>
                <div className="px-5">
                    <div className="flex items-end justify-between mb-6">
                        <h2
                            className="text-xl font-bold"
                            style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#111111' }}
                        >
                            New Arrivals
                        </h2>
                        <button
                            onClick={() => navigate('/template25/shop')}
                            className="text-xs font-semibold tracking-[0.1em] uppercase inline-flex items-center gap-1"
                            style={{ color: '#0D7C66', background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                            View All <ArrowRight className="w-3 h-3" />
                        </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template25/shop')}
                                className="cursor-pointer group"
                                style={{ borderRadius: 0 }}
                            >
                                <div
                                    className="relative w-full aspect-square overflow-hidden mb-2"
                                    style={{ borderRadius: 0 }}
                                >
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover"
                                    />
                                    {/* Wishlist */}
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center transition-all duration-300"
                                        style={{
                                            backgroundColor: 'rgba(255,255,255,0.9)',
                                            border: 'none',
                                            borderRadius: 0,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <Heart
                                            size={16}
                                            style={{
                                                color: wishlist[product.id] ? '#0D7C66' : '#6C757D',
                                                fill: wishlist[product.id] ? '#0D7C66' : 'none',
                                            }}
                                        />
                                    </button>
                                </div>
                                <h3
                                    className="text-xs font-medium mb-1 leading-tight"
                                    style={{
                                        color: '#111111',
                                        fontFamily: "'IBM Plex Sans', sans-serif",
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {product.name}
                                </h3>
                                <p
                                    className="text-sm font-bold"
                                    style={{ color: '#0D7C66', fontFamily: "'Space Grotesk', sans-serif" }}
                                >
                                    {product.price}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Desktop: 4 columns */}
            <section className="hidden lg:block py-20" style={{ backgroundColor: '#FFFFFF' }}>
                <div className="max-w-7xl mx-auto px-8">
                    <div className="flex items-end justify-between mb-12">
                        <h2
                            className="text-3xl font-bold"
                            style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#111111' }}
                        >
                            New Arrivals
                        </h2>
                        <button
                            onClick={() => navigate('/template25/shop')}
                            className="text-sm font-semibold tracking-[0.1em] uppercase inline-flex items-center gap-2 transition-all duration-300"
                            style={{ color: '#0D7C66', background: 'none', border: 'none', cursor: 'pointer' }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#0A9B7F'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = '#0D7C66'; }}
                        >
                            View All <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="grid grid-cols-4 gap-6">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template25/shop')}
                                className="cursor-pointer group"
                            >
                                <div
                                    className="relative w-full aspect-square overflow-hidden mb-4 transition-all duration-300"
                                    style={{
                                        borderRadius: 0,
                                        border: '2px solid transparent',
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#0D7C66'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; }}
                                >
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                    {/* Wishlist */}
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center transition-all duration-300"
                                        style={{
                                            backgroundColor: 'rgba(255,255,255,0.9)',
                                            border: 'none',
                                            borderRadius: 0,
                                            cursor: 'pointer',
                                            opacity: 0,
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#FFFFFF'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.9)'; }}
                                    >
                                        <Heart
                                            size={18}
                                            style={{
                                                color: wishlist[product.id] ? '#0D7C66' : '#6C757D',
                                                fill: wishlist[product.id] ? '#0D7C66' : 'none',
                                            }}
                                        />
                                    </button>
                                    <style>{`
                                        .group:hover button { opacity: 1 !important; }
                                    `}</style>
                                </div>
                                <h3
                                    className="text-sm font-medium mb-1"
                                    style={{
                                        color: '#111111',
                                        fontFamily: "'IBM Plex Sans', sans-serif",
                                    }}
                                >
                                    {product.name}
                                </h3>
                                <p
                                    className="text-base font-bold"
                                    style={{ color: '#0D7C66', fontFamily: "'Space Grotesk', sans-serif" }}
                                >
                                    {product.price}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 4: STATS BAR ===== */}
            {/* Mobile */}
            <section className="lg:hidden" style={{ backgroundColor: '#111111' }}>
                <div className="grid grid-cols-2 gap-0">
                    {stats.map((stat, index) => (
                        <div
                            key={index}
                            className="py-8 px-5 text-center"
                            style={{
                                borderRight: index % 2 === 0 ? '1px solid #2A2A2A' : 'none',
                                borderBottom: index < 2 ? '1px solid #2A2A2A' : 'none',
                            }}
                        >
                            <p
                                className="text-2xl font-bold mb-1"
                                style={{ color: '#0D7C66', fontFamily: "'Space Grotesk', sans-serif" }}
                            >
                                {stat.number}
                            </p>
                            <p
                                className="text-xs tracking-[0.1em] uppercase"
                                style={{ color: '#FFFFFF', fontFamily: "'IBM Plex Sans', sans-serif" }}
                            >
                                {stat.label}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Desktop */}
            <section className="hidden lg:block" style={{ backgroundColor: '#111111' }}>
                <div className="max-w-7xl mx-auto px-8">
                    <div className="grid grid-cols-4 divide-x" style={{ borderColor: '#2A2A2A' }}>
                        {stats.map((stat, index) => (
                            <div
                                key={index}
                                className="py-16 text-center"
                                style={{ borderColor: '#2A2A2A' }}
                            >
                                <p
                                    className="text-4xl font-bold mb-2"
                                    style={{ color: '#0D7C66', fontFamily: "'Space Grotesk', sans-serif" }}
                                >
                                    {stat.number}
                                </p>
                                <p
                                    className="text-sm tracking-[0.15em] uppercase"
                                    style={{ color: '#FFFFFF', fontFamily: "'IBM Plex Sans', sans-serif" }}
                                >
                                    {stat.label}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 5: BESTSELLERS SPOTLIGHT ===== */}
            {/* Mobile: stacked */}
            <section className="lg:hidden py-10" style={{ backgroundColor: '#F8F9FA' }}>
                <div className="px-5">
                    <h2
                        className="text-xl font-bold mb-6"
                        style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#111111' }}
                    >
                        Bestsellers
                    </h2>
                    <div className="flex flex-col gap-5">
                        {bestsellers.map((item) => (
                            <div
                                key={item.id}
                                onClick={() => navigate('/template25/shop')}
                                className="cursor-pointer"
                                style={{ backgroundColor: '#FFFFFF', borderRadius: 0 }}
                            >
                                <div className="relative w-full aspect-[4/3] overflow-hidden" style={{ borderRadius: 0 }}>
                                    <img
                                        src={item.image}
                                        alt={item.name}
                                        className="w-full h-full object-cover"
                                    />
                                    {/* Bestseller Badge */}
                                    <span
                                        className="absolute top-3 left-3 px-3 py-1.5 text-xs font-bold tracking-[0.1em] uppercase"
                                        style={{
                                            backgroundColor: '#0D7C66',
                                            color: '#FFFFFF',
                                            borderRadius: 0,
                                            fontFamily: "'IBM Plex Sans', sans-serif",
                                        }}
                                    >
                                        Bestseller
                                    </span>
                                </div>
                                <div className="p-4">
                                    <h3
                                        className="text-base font-semibold mb-2"
                                        style={{ color: '#111111', fontFamily: "'Space Grotesk', sans-serif" }}
                                    >
                                        {item.name}
                                    </h3>
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="flex gap-0.5">
                                            {[...Array(5)].map((_, i) => (
                                                <Star
                                                    key={i}
                                                    size={14}
                                                    style={{
                                                        color: i < Math.floor(item.rating) ? '#0D7C66' : '#DEE2E6',
                                                        fill: i < Math.floor(item.rating) ? '#0D7C66' : 'none',
                                                    }}
                                                />
                                            ))}
                                        </div>
                                        <span className="text-xs" style={{ color: '#6C757D' }}>
                                            ({item.reviews})
                                        </span>
                                    </div>
                                    <p
                                        className="text-lg font-bold"
                                        style={{ color: '#0D7C66', fontFamily: "'Space Grotesk', sans-serif" }}
                                    >
                                        {item.price}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Desktop: 3 columns */}
            <section className="hidden lg:block py-20" style={{ backgroundColor: '#F8F9FA' }}>
                <div className="max-w-7xl mx-auto px-8">
                    <div className="flex items-end justify-between mb-12">
                        <h2
                            className="text-3xl font-bold"
                            style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#111111' }}
                        >
                            Bestsellers
                        </h2>
                        <button
                            onClick={() => navigate('/template25/shop')}
                            className="text-sm font-semibold tracking-[0.1em] uppercase inline-flex items-center gap-2 transition-all duration-300"
                            style={{ color: '#0D7C66', background: 'none', border: 'none', cursor: 'pointer' }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#0A9B7F'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = '#0D7C66'; }}
                        >
                            Shop All <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="grid grid-cols-3 gap-8">
                        {bestsellers.map((item) => (
                            <div
                                key={item.id}
                                onClick={() => navigate('/template25/shop')}
                                className="cursor-pointer group"
                                style={{ backgroundColor: '#FFFFFF', borderRadius: 0 }}
                            >
                                <div
                                    className="relative w-full aspect-[4/3] overflow-hidden transition-all duration-300"
                                    style={{
                                        borderRadius: 0,
                                        border: '2px solid transparent',
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#0D7C66'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; }}
                                >
                                    <img
                                        src={item.image}
                                        alt={item.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                    {/* Bestseller Badge */}
                                    <span
                                        className="absolute top-4 left-4 px-4 py-2 text-xs font-bold tracking-[0.1em] uppercase"
                                        style={{
                                            backgroundColor: '#0D7C66',
                                            color: '#FFFFFF',
                                            borderRadius: 0,
                                            fontFamily: "'IBM Plex Sans', sans-serif",
                                        }}
                                    >
                                        Bestseller
                                    </span>
                                </div>
                                <div className="p-6">
                                    <h3
                                        className="text-lg font-semibold mb-3"
                                        style={{ color: '#111111', fontFamily: "'Space Grotesk', sans-serif" }}
                                    >
                                        {item.name}
                                    </h3>
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="flex gap-0.5">
                                            {[...Array(5)].map((_, i) => (
                                                <Star
                                                    key={i}
                                                    size={16}
                                                    style={{
                                                        color: i < Math.floor(item.rating) ? '#0D7C66' : '#DEE2E6',
                                                        fill: i < Math.floor(item.rating) ? '#0D7C66' : 'none',
                                                    }}
                                                />
                                            ))}
                                        </div>
                                        <span
                                            className="text-sm font-medium"
                                            style={{ color: '#0D7C66' }}
                                        >
                                            {item.rating}
                                        </span>
                                        <span className="text-sm" style={{ color: '#6C757D' }}>
                                            ({item.reviews} reviews)
                                        </span>
                                    </div>
                                    <p
                                        className="text-xl font-bold"
                                        style={{ color: '#0D7C66', fontFamily: "'Space Grotesk', sans-serif" }}
                                    >
                                        {item.price}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 6: SERVICE FEATURES ===== */}
            {/* Mobile: 2x2 grid */}
            <section className="lg:hidden py-10" style={{ backgroundColor: '#FFFFFF' }}>
                <div className="px-5">
                    <div className="grid grid-cols-2 gap-4">
                        {services.map((service, index) => {
                            const IconComp = service.icon;
                            return (
                                <div
                                    key={index}
                                    className="p-5 text-center"
                                    style={{
                                        backgroundColor: '#F8F9FA',
                                        borderRadius: 0,
                                    }}
                                >
                                    <div
                                        className="w-10 h-10 mx-auto mb-3 flex items-center justify-center"
                                        style={{ backgroundColor: '#E8F5F1', borderRadius: 0 }}
                                    >
                                        <IconComp size={20} style={{ color: '#0D7C66' }} />
                                    </div>
                                    <h4
                                        className="text-xs font-bold tracking-[0.05em] uppercase mb-1"
                                        style={{ color: '#111111', fontFamily: "'Space Grotesk', sans-serif" }}
                                    >
                                        {service.title}
                                    </h4>
                                    <p className="text-xs" style={{ color: '#6C757D' }}>
                                        {service.desc}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Desktop: 4 columns */}
            <section className="hidden lg:block py-20" style={{ backgroundColor: '#FFFFFF', borderTop: '1px solid #DEE2E6', borderBottom: '1px solid #DEE2E6' }}>
                <div className="max-w-7xl mx-auto px-8">
                    <div className="grid grid-cols-4 gap-8">
                        {services.map((service, index) => {
                            const IconComp = service.icon;
                            return (
                                <div
                                    key={index}
                                    className="text-center p-8 transition-all duration-300"
                                    style={{ backgroundColor: '#FFFFFF', borderRadius: 0 }}
                                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F8F9FA'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FFFFFF'; }}
                                >
                                    <div
                                        className="w-14 h-14 mx-auto mb-5 flex items-center justify-center"
                                        style={{ backgroundColor: '#E8F5F1', borderRadius: 0 }}
                                    >
                                        <IconComp size={24} style={{ color: '#0D7C66' }} />
                                    </div>
                                    <h4
                                        className="text-sm font-bold tracking-[0.1em] uppercase mb-2"
                                        style={{ color: '#111111', fontFamily: "'Space Grotesk', sans-serif" }}
                                    >
                                        {service.title}
                                    </h4>
                                    <p className="text-sm leading-relaxed" style={{ color: '#6C757D' }}>
                                        {service.desc}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 7: NEWSLETTER ===== */}
            {/* Mobile */}
            <section className="lg:hidden py-12" style={{ backgroundColor: '#111111' }}>
                <div className="px-5 text-center">
                    <div className="w-10 h-0.5 mx-auto mb-6" style={{ backgroundColor: '#0D7C66' }} />
                    <h2
                        className="text-2xl font-bold mb-3"
                        style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#FFFFFF' }}
                    >
                        Stay in the Loop
                    </h2>
                    <p className="text-sm mb-6" style={{ color: '#6C757D' }}>
                        New collections, exclusive offers, and style inspiration delivered to your inbox.
                    </p>
                    <div className="flex flex-col gap-3 max-w-sm mx-auto">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="px-5 py-3.5 text-sm focus:outline-none"
                            style={{
                                backgroundColor: '#111111',
                                border: '1px solid #2A2A2A',
                                color: '#FFFFFF',
                                borderRadius: 0,
                                fontFamily: "'IBM Plex Sans', sans-serif",
                            }}
                        />
                        <button
                            className="w-full px-8 py-3.5 text-sm tracking-[0.15em] uppercase font-semibold transition-all duration-300"
                            style={{
                                backgroundColor: '#0D7C66',
                                color: '#FFFFFF',
                                border: 'none',
                                borderRadius: 0,
                                fontFamily: "'IBM Plex Sans', sans-serif",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#0A9B7F'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#0D7C66'; }}
                        >
                            Subscribe
                        </button>
                    </div>
                </div>
            </section>

            {/* Desktop */}
            <section className="hidden lg:block py-24" style={{ backgroundColor: '#111111' }}>
                <div className="max-w-2xl mx-auto px-8 text-center">
                    <div className="w-12 h-0.5 mx-auto mb-8" style={{ backgroundColor: '#0D7C66' }} />
                    <h2
                        className="text-4xl font-bold mb-4"
                        style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#FFFFFF' }}
                    >
                        Stay in the Loop
                    </h2>
                    <p className="text-base mb-10" style={{ color: '#6C757D' }}>
                        New collections, exclusive offers, and style inspiration delivered to your inbox.
                    </p>
                    <div className="flex gap-0 max-w-md mx-auto">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="flex-1 px-6 py-4 text-sm focus:outline-none"
                            style={{
                                backgroundColor: '#111111',
                                border: '1px solid #2A2A2A',
                                borderRight: 'none',
                                color: '#FFFFFF',
                                borderRadius: 0,
                                fontFamily: "'IBM Plex Sans', sans-serif",
                            }}
                        />
                        <button
                            className="px-8 py-4 text-sm tracking-[0.15em] uppercase font-semibold transition-all duration-300"
                            style={{
                                backgroundColor: '#0D7C66',
                                color: '#FFFFFF',
                                border: 'none',
                                borderRadius: 0,
                                fontFamily: "'IBM Plex Sans', sans-serif",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#0A9B7F'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#0D7C66'; }}
                        >
                            Subscribe
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Template25;
